import { glom } from '../../../Signage/glom';
import { ReachAPI } from '../../../ReachApi';
import { DateTime } from 'luxon';
import {
    PlaceExchangeWidgetConfigurationDetails,
    PlaceExchangeWidgetConfiguration,
    ZodPlaceExchangeWidgetConfigurationDetails,
    PlaceExchangeWidgetTimelineSlot,
    PlaceExchangeAd,
} from './types';
import { WidgetType } from '../../types';
import { Log } from '../../../AuxillaryService/Log';
import { GetCredentialsAPISuccessResponse } from '../../../ReachApi/placeExchangeTypes';
import { PlaceExchangeApiHelper } from './PlaceExchangeApiHelper';
import { createPlaceExchangeVideoHandler } from '../../../MediaController';
import AdQueue from '../AdQueue';
import { PlayerTokenError } from '../errors';
import { BaseThirdPartyAdWidget } from '../BaseThirdPartyAdWidget';
import { PlaceExchangeVideoHandler } from '../../../MediaController/Video';

export class PlaceExchangeWidget extends BaseThirdPartyAdWidget {
    type: WidgetType.PLACE_EXCHANGE;
    declare config: PlaceExchangeWidgetConfigurationDetails;

    protected readonly adQueue: AdQueue<PlaceExchangeAd> = new AdQueue();

    /**
     * Embed token for the resource
     */
    #accessToken: string | null;

    /**
     * Embed token expiry time (format: 'YYYY-MM-DDTHH:mm:ssZ')
     */
    #accessTokenExpiration: string | null;

    /**
     *  Place exchange organization id
     */
    #organizationId: string;

    /**
     *
     * Place Exchange adunit name
     */
    #adunitName: string | null;

    constructor(widgetConfiguration: PlaceExchangeWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.type = WidgetType.PLACE_EXCHANGE;
        this.#accessToken = null;
        this.#accessTokenExpiration = null;
        this.#adunitName = null;
        this.#organizationId = this.config.organization_id;
    }

    protected validateConfigurationDetails(
        placeExchangeWidgetConfigurationDetails: PlaceExchangeWidgetConfigurationDetails
    ): PlaceExchangeWidgetConfigurationDetails {
        return ZodPlaceExchangeWidgetConfigurationDetails.parse(
            placeExchangeWidgetConfigurationDetails
        );
    }

    /**
     * Get credentials used for Place Exchange
     * api requests
     * @throws {@link PlayerTokenError} when token player token or
     * player UUID id aren't set.
     * @throws {@link PlaceExchangeCredentialsError} when retrieval of
     * Place Exchange credentials.
     */
    private async getPlaceExchangeCredentials(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new PlayerTokenError('Player uuid or security token must be set');
        }
        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );
        this.#accessToken = null;
        this.#accessTokenExpiration = null;
        try {
            const data: GetCredentialsAPISuccessResponse =
                await ReachAPI.placeexchange.getCredentials(security, this.id);

            if (!data.data.credentials.token.access_token || !data.data.credentials.adunit_name) {
                Log.Warning(`${this.name} Invalid credentials received`, null, __fileline);
                return;
            }

            this.#accessToken = data.data.credentials.token.access_token;
            const expiryDate: string = this.calculateAccessTokenExpiryDate(
                Number(data.data.credentials.token.expires_in)
            );

            this.#accessTokenExpiration = expiryDate;
            this.#adunitName = data.data.credentials.adunit_name;

            Log.Info(
                `Place Exchange: successfully retrieved place exchange API credentials`,
                null,
                __fileline
            );
        } catch (error) {
            Log.Warning(`${this.name}: Failed to retrieve credentials`, null, __fileline);
        }
    }

    /**
     * Check if Place Exchange token is valid
     */
    private isTokenValid(): boolean {
        if (this.#accessTokenExpiration === null || !this.#accessToken) {
            Log.Info(`Place Exchange: token expired or invalid`, null, __fileline);
            return false;
        }
        const expiration = DateTime.fromISO(this.#accessTokenExpiration);
        if (expiration.diffNow('minutes').minutes < 10) {
            Log.Info(
                `Place Exchange: Expiration in ${expiration.diffNow('minutes').minutes} minutes.
                Expiration is set at ${expiration.toSQL()}`,
                null,
                __fileline
            );
            return false;
        }

        Log.Info('Place Exchange: Token is valid till ' + expiration.toSQL(), null, __fileline);
        return true;
    }

    /**
     * send proof of play and remove played
     * ads from dom,  will also fetch ads if the current
     * slot it the last slot place exchange slot in the current loop
     */
    async afterFadeOut(currentSlot: PlaceExchangeWidgetTimelineSlot): Promise<void> {
        if (
            !currentSlot.params.ad.isFallback &&
            currentSlot.params.ad.playcount >= 1 &&
            this.#adunitName
        ) {
            if (this.#accessToken) {
                await PlaceExchangeApiHelper.sendProofOfPlay(
                    currentSlot.params.ad,
                    this.#organizationId,
                    this.#adunitName,
                    Math.floor(this.startTime / 1000),
                    this.#accessToken
                );
            }
        }

        await super.afterFadeOut(currentSlot);
    }

    /**
     *
     * calculates the expiry date of
     * PE access token and returns it in
     * ISO format
     * i.e "2023-03-21T13:58:31.266Z"
     */
    private calculateAccessTokenExpiryDate(durationInSeconds: number): string {
        const now: number = Date.now();
        const laterInEpochTime = now + durationInSeconds * 1000;
        const expiryDate = new Date(laterInEpochTime).toISOString();
        return expiryDate;
    }

    /**
     * Fetches place exchange ads.
     */
    async getAds(): Promise<void> {
        const tokenIsValid = this.isTokenValid();
        if (tokenIsValid === false) {
            await this.getPlaceExchangeCredentials();
        }
        if (
            this.#accessToken !== null &&
            this.#organizationId !== null &&
            this.#adunitName !== null
        ) {
            await PlaceExchangeApiHelper.fetchAds(
                this.#organizationId,
                this.#accessToken,
                this.adHeight,
                this.adWidth,
                this.durationInSeconds,
                this.querySlotsCallNo === 1 ? this.numberOfAds * 2 : this.numberOfAds,
                this.#adunitName,
                this.adQueue
            );
        } else {
            Log.Error(
                'Place Exchange: failed to fetch ads, organization ID or accessToken not set.',
                null,
                __fileline
            );
        }
    }

    getThirdPartyAdVendorVideoHandler(assetUrl: string): PlaceExchangeVideoHandler {
        return createPlaceExchangeVideoHandler({
            videoUrl: assetUrl,
            muted: this.muteAds,
        });
    }
}
